import React from 'react';
import { getBrowserSpeechSupport } from '../../utils/speechRecognition';

// Inline SVG icons
const MicrophoneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const StopIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const ExclamationTriangleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

/**
 * Röstinspelningsknapp med visuell feedback
 *
 * @param {Object} props
 * @param {boolean} props.isListening - Om inspelning pågår
 * @param {boolean} props.isSupported - Om röstinmatning stöds
 * @param {string} props.error - Eventuellt felmeddelande
 * @param {string} props.interimTranscript - Pågående transkribering
 * @param {function} props.onStart - Callback för att starta inspelning
 * @param {function} props.onStop - Callback för att stoppa inspelning
 * @param {boolean} props.disabled - Om knappen är disabled
 * @param {string} props.size - Storlek: 'sm', 'md', 'lg' (default: 'md')
 */
export function VoiceRecorder({
  isListening,
  isSupported,
  error,
  interimTranscript,
  onStart,
  onStop,
  disabled = false,
  size = 'md'
}) {
  const browserSupport = getBrowserSpeechSupport();

  // Storlekar
  const sizeClasses = {
    sm: {
      button: 'p-1.5',
      icon: 'w-4 h-4'
    },
    md: {
      button: 'p-2',
      icon: 'w-5 h-5'
    },
    lg: {
      button: 'p-3',
      icon: 'w-6 h-6'
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  // Om inte stöds, visa disabled med tooltip
  if (!isSupported) {
    return (
      <div className="relative group">
        <button
          disabled
          className={`${currentSize.button} rounded-full bg-gray-100 text-gray-400 cursor-not-allowed`}
          title={browserSupport.message}
        >
          <MicrophoneIcon className={`${currentSize.icon} opacity-50`} />
        </button>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-800 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          {browserSupport.message}
        </div>
      </div>
    );
  }

  // Felstatus
  if (error) {
    return (
      <div className="relative group">
        <button
          onClick={onStart}
          disabled={disabled}
          className={`${currentSize.button} rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors`}
          title={error}
        >
          <ExclamationTriangleIcon className={currentSize.icon} />
        </button>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-red-600 text-white rounded max-w-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          {error}
        </div>
      </div>
    );
  }

  // Inspelning pågår
  if (isListening) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onStop}
          className={`${currentSize.button} rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors animate-pulse`}
          title="Stoppa inspelning"
        >
          <StopIcon className={currentSize.icon} />
        </button>

        {/* Waveform animation */}
        <div className="flex items-center gap-0.5">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-red-500 rounded-full animate-pulse"
              style={{
                height: `${8 + Math.random() * 12}px`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>

        {/* Interim transcript preview */}
        {interimTranscript && (
          <span className="text-sm text-gray-500 italic max-w-[150px] truncate">
            {interimTranscript}
          </span>
        )}
      </div>
    );
  }

  // Idle state
  return (
    <button
      onClick={onStart}
      disabled={disabled}
      className={`${currentSize.button} rounded-full bg-gray-100 text-gray-600
        hover:bg-leadme-pink hover:text-white transition-colors
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 disabled:hover:text-gray-600`}
      title="Starta röstinmatning"
    >
      <MicrophoneIcon className={currentSize.icon} />
    </button>
  );
}

export default VoiceRecorder;
